import { useAuth } from "@/features/auth";
import { topicsForDomain } from "@/infrastructure/rsocket";
import { Badge, Button, Collapsible, CollapsibleContent, CollapsibleTrigger, Icon } from "@/shared/components";
import { ApiDomain, cn } from "@/shared/utils";
import { Activity, Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useRSocketContext } from "../hooks";
import { ConnectionStatus, Event } from "../types";

const DEFAULT_HEIGHT = 300;
const MIN_HEIGHT = 240;
const MAX_HEIGHT = 1200;

const TOPIC_COLORS: Record<string, string> = {
    acties: "#a6cee3",
    berichten: "#1f78b4",
    contactpersonen: "#b2df8a",
    betrokkenStations: "#33a02c",
    betrokkenTreinen: "#fb9a99",
    details: "#e31a1c",
    overzicht: "#fdbf6f",
    logboek: "#ff7f00",
    opstartplan: "#cab2d6",
    suggesties: "#6a3d9a",
    vestigingen: "#ffff99",
    vsm: "#b15928",
    "ongewenste-gebeurtenis": "#ff3082",
};

interface TerminalMessage {
    id: string;
    topic: string;
    timestamp: number;
    data: unknown;
}

interface TerminalState {
    selectedTopic: string | null;
    messages: TerminalMessage[];
    expandedMessages: Set<string>;
}

type TerminalAction =
    | { type: "SELECT_TOPIC"; payload: string | null }
    | { type: "ADD_MESSAGE"; payload: { topic: string; data: unknown } }
    | { type: "CLEAR_MESSAGES" }
    | { type: "TOGGLE_MESSAGE"; payload: string }
    | { type: "EXPAND_ALL" }
    | { type: "COLLAPSE_ALL" };

const MAX_MESSAGES = 100;

function terminalReducer(state: TerminalState, action: TerminalAction): TerminalState {
    switch (action.type) {
        case "SELECT_TOPIC":
            return { ...state, selectedTopic: action.payload };
        case "ADD_MESSAGE": {
            const newMessage: TerminalMessage = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                topic: action.payload.topic,
                timestamp: Date.now(),
                data: action.payload.data,
            };
            const messages = [...state.messages, newMessage].slice(-MAX_MESSAGES);
            return {
                ...state,
                messages,
            };
        }
        case "CLEAR_MESSAGES":
            return { ...state, messages: [], expandedMessages: new Set() };
        case "TOGGLE_MESSAGE": {
            const expanded = new Set(state.expandedMessages);
            if (expanded.has(action.payload)) {
                expanded.delete(action.payload);
            } else {
                expanded.add(action.payload);
            }
            return { ...state, expandedMessages: expanded };
        }
        case "EXPAND_ALL": {
            const allIds = new Set(state.messages.map((m) => m.id));
            return { ...state, expandedMessages: allIds };
        }
        case "COLLAPSE_ALL":
            return { ...state, expandedMessages: new Set() };
        default:
            return state;
    }
}

const initialState: TerminalState = {
    selectedTopic: null,
    messages: [],
    expandedMessages: new Set(),
};

function getTopicRole(topic: string): string {
    const parts = topic.split(".");
    return parts[0] || topic;
}

function getTopicChannel(topic: string): string {
    const parts = topic.split(".");
    return parts.length > 1 ? parts.slice(1).join(".") : topic;
}

function getTopicColor(topic: string): string {
    const channel = getTopicChannel(topic);
    const channelSuffix = channel.split(".").pop();
    for (const [key, color] of Object.entries(TOPIC_COLORS)) {
        if (channelSuffix?.includes(key)) {
            return color;
        }
    }
    return "#6b7280";
}

function formatTimestamp(timestamp: number): string {
    return new Date(timestamp).toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
    });
}

type SubscriptionStatus = "pending" | "subscribed" | "failed";
function getStatusLabel(subStatus: SubscriptionStatus | undefined) {
    switch (subStatus) {
        case "subscribed":
            return null;
        case "failed":
            return "failed";
        case "pending":
            return "pending";
        default:
            return "waiting";
    }
}

export default function WebsocketTerminal() {
    const { apiDomain } = useAuth();
    const [open, setOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    return (
        <>
            <WebSocketTerminalTrigger toggle={() => setOpen((prev) => !prev)} isOpen={open} unreadCount={unreadCount} />
            <Activity mode={open ? "visible" : "hidden"}>
                <WebSocketTerminalPanel setUnreadCount={setUnreadCount} closeTerminal={() => setOpen(false)} apiDomain={apiDomain} />
            </Activity>
        </>
    );
}

type WebSocketTerminalTriggerProps = {
    toggle: () => void;
    isOpen: boolean;
    unreadCount: number;
};

export function WebSocketTerminalTrigger({ toggle, isOpen, unreadCount }: WebSocketTerminalTriggerProps) {
    return (
        <div className="fixed right-3 bottom-18 [z-index:9999999]">
            <Button onClick={toggle} className="h-12 w-12 !rounded-full shadow-lg" title="WebSocket Terminal" iconOnly icon="switch" />
            {!isOpen && unreadCount > 0 && (
                <span className="bg-brand-primary text-content-default text-s absolute -top-1 -right-1 flex h-6 min-w-6 items-center justify-center rounded-full px-1 font-bold">
                    {unreadCount > 99 ? "99+" : unreadCount}
                </span>
            )}
        </div>
    );
}

type WebSocketTerminalPanelProps = {
    setUnreadCount: Dispatch<SetStateAction<number>>;
    closeTerminal: () => void;
    apiDomain: ApiDomain | null;
};

export function WebSocketTerminalPanel({ apiDomain, setUnreadCount, closeTerminal }: WebSocketTerminalPanelProps) {
    const { isConnected, status, subscribeTopic } = useRSocketContext();
    const [state, dispatch] = useReducer(terminalReducer, initialState);
    const [height, setHeight] = useState(DEFAULT_HEIGHT);
    const [isDragging, setIsDragging] = useState(false);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [topicStatuses, setTopicStatuses] = useState<Map<string, SubscriptionStatus>>(new Map());
    const [lastError, setLastError] = useState<string | null>(null);
    const subscriptionsRef = useRef<Map<string, { cancel: () => void }>>(new Map());
    const contentRef = useRef<HTMLDivElement>(null);
    const dragStartY = useRef(0);
    const dragStartHeight = useRef(0);

    const topics = useMemo<string[]>(() => (apiDomain ? topicsForDomain(apiDomain) : []), [apiDomain]);
    const activeSubscriptions = useMemo(() => {
        return Array.from(topicStatuses.values()).filter((s) => s === "subscribed").length;
    }, [topicStatuses]);

    const statusConfig = {
        [ConnectionStatus.CONNECTED]: { color: "bg-success-default", label: "Connected" },
        [ConnectionStatus.CONNECTING]: { color: "bg-accent-yellow-default", label: "Connecting..." },
        [ConnectionStatus.DISCONNECTED]: { color: "bg-emphasis-secondary", label: "Disconnected" },
        [ConnectionStatus.ERROR]: { color: "bg-error-default", label: "Error" },
    }[status];

    const handleDragStart = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setIsDragging(true);
            dragStartY.current = e.clientY;
            dragStartHeight.current = height;
        },
        [height]
    );

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const delta = dragStartY.current - e.clientY;
            const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, dragStartHeight.current + delta));
            setHeight(newHeight);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging]);

    const filteredMessages = state.selectedTopic ? state.messages.filter((m) => m.topic === state.selectedTopic) : state.messages;

    const copyToClipboard = useCallback((text: string, id: string) => {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                setCopiedId(id);
                setTimeout(() => setCopiedId(null), 1500);
            })
            .catch(console.error);
    }, []);

    const copyAllMessages = useCallback(() => {
        const text = filteredMessages.map((m) => JSON.stringify(m.data)).join("\n");
        copyToClipboard(text, "all");
    }, [filteredMessages, copyToClipboard]);

    const handleMessage = useCallback(
        (topic: string, data: unknown) => {
            dispatch({ type: "ADD_MESSAGE", payload: { topic, data } });
            setUnreadCount((prev) => prev + 1);
        },
        [setUnreadCount]
    );

    const failedCount = useMemo(() => {
        return Array.from(topicStatuses.values()).filter((s) => s === "failed").length;
    }, [topicStatuses]);

    // Prevent effect re-runs on token refresh
    const subscribeTopicRef = useRef(subscribeTopic);
    useEffect(() => {
        subscribeTopicRef.current = subscribeTopic;
    }, [subscribeTopic]);

    const subscribingTopicsRef = useRef<Set<string>>(new Set());

    const updateTopicStatus = useCallback((topic: string, newStatus: SubscriptionStatus) => {
        setTopicStatuses((prev) => {
            const next = new Map(prev);
            next.set(topic, newStatus);
            return next;
        });
    }, []);

    const retryFailedSubscriptions = useCallback(async () => {
        if (!isConnected) return;

        const failedTopics = Array.from(topicStatuses.entries())
            .filter(([, status]) => status === "failed")
            .map(([topic]) => topic);

        setLastError(null);

        for (const topic of failedTopics) {
            if (subscriptionsRef.current.has(topic)) continue;

            updateTopicStatus(topic, "pending");

            try {
                const subscription = await subscribeTopicRef.current(topic, (message: Event) => {
                    handleMessage(topic, message);
                });
                subscriptionsRef.current.set(topic, subscription);
                updateTopicStatus(topic, "subscribed");
            } catch (error) {
                console.error(`Retry failed for topic ${topic}:`, error);
                updateTopicStatus(topic, "failed");
                setLastError(`Retry failed for ${getTopicChannel(topic)}`);
            }
        }
    }, [isConnected, topicStatuses, updateTopicStatus, handleMessage]);

    useEffect(() => {
        if (!isConnected || topics.length === 0) return;

        let isCancelled = false;
        setLastError(null);

        const currentSubscriptions = subscriptionsRef.current;
        const currentSubscribingTopics = subscribingTopicsRef.current;
        const subscribeToTopics = async () => {
            for (const topic of topics) {
                if (isCancelled) break;
                if (currentSubscriptions.has(topic)) continue;
                if (currentSubscribingTopics.has(topic)) continue;

                currentSubscribingTopics.add(topic);
                updateTopicStatus(topic, "pending");

                try {
                    const subscription = await subscribeTopicRef.current(topic, (message: Event) => {
                        handleMessage(topic, message);
                    });

                    if (isCancelled) {
                        subscription.cancel();
                    } else {
                        currentSubscriptions.set(topic, subscription);
                        updateTopicStatus(topic, "subscribed");
                    }
                } catch (error) {
                    console.error(`Failed to subscribe to topic ${topic}:`, error);
                    updateTopicStatus(topic, "failed");
                    setLastError(`Failed to subscribe to ${getTopicChannel(topic)}`);
                } finally {
                    currentSubscribingTopics.delete(topic);
                }
            }
        };

        void subscribeToTopics();

        return () => {
            isCancelled = true;
            currentSubscriptions.forEach((sub) => sub.cancel());
            currentSubscriptions.clear();
            currentSubscribingTopics.clear();
            setTopicStatuses(new Map());
        };
    }, [isConnected, topics, handleMessage, updateTopicStatus]);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
    }, [state.messages]);

    const getMessageCountForTopic = (topic: string | null): number => {
        if (topic === null) return state.messages.length;
        return state.messages.filter((m) => m.topic === topic).length;
    };

    const clearAll = () => {
        dispatch({ type: "CLEAR_MESSAGES" });
        setUnreadCount(0);
    };

    return (
        <div
            className="border-content-default-inverse drop-shadow-elevated-2 text-content-default bg-content-default-inverse relative z-40 shrink-0 shadow-2xl"
            style={{ height }}
        >
            <div
                className="hover:bg-selected-emphasis-hover bg-selected-emphasis-default absolute -top-1 [z-index:999999999] flex h-2 w-full cursor-ns-resize items-center justify-center"
                onMouseDown={handleDragStart}
            />

            <Toolbar
                statusConfig={statusConfig}
                activeSubscriptions={activeSubscriptions}
                strings={topics}
                state={state}
                lastError={lastError}
                apiDomain={apiDomain}
                failedCount={failedCount}
                retryFailedSubscriptions={() => void retryFailedSubscriptions()}
                copiedId={copiedId}
                terminalMessages={filteredMessages}
                copyAllMessages={copyAllMessages}
                expandAll={() => dispatch({ type: "EXPAND_ALL" })}
                collapseAll={() => dispatch({ type: "COLLAPSE_ALL" })}
                clearAll={clearAll}
                closeTerminal={closeTerminal}
            />

            <div className="flex" style={{ height: height - 40 }}>
                <Sidebar
                    state={state}
                    selectTopic={() => dispatch({ type: "SELECT_TOPIC", payload: null })}
                    messageCountForTopic={getMessageCountForTopic(null)}
                >
                    {topics.map((topic) => {
                        const subStatus = topicStatuses.get(topic);
                        const statusLabel = getStatusLabel(subStatus);

                        return (
                            <button
                                key={topic}
                                onClick={() => dispatch({ type: "SELECT_TOPIC", payload: topic })}
                                className={cn(
                                    "hover:bg-hover flex w-full cursor-pointer items-center gap-1 px-2 py-1 text-left text-xs",
                                    state.selectedTopic === topic ? `bg-hover` : "bg-content-default-inverse"
                                )}
                            >
                                <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: getTopicColor(topic) }} />
                                <span className="flex-1 truncate font-mono">{getTopicChannel(topic)}</span>
                                {statusLabel && (
                                    <span
                                        className={cn("text-[10px]", {
                                            "text-content-error": subStatus === "failed",
                                            "text-content-placeholder": subStatus !== "failed",
                                        })}
                                    >
                                        [{statusLabel}]
                                    </span>
                                )}
                                {getMessageCountForTopic(topic) > 0 && (
                                    <Badge variant="gray-subtle" size="s">
                                        {getMessageCountForTopic(topic)}
                                    </Badge>
                                )}
                            </button>
                        );
                    })}
                </Sidebar>

                <Content ref={contentRef} terminalMessages={filteredMessages} statusConfig={statusConfig} connected={isConnected}>
                    {filteredMessages.map((msg) => (
                        <MessageRow
                            key={msg.id}
                            message={msg}
                            isExpanded={state.expandedMessages.has(msg.id)}
                            onToggle={() => dispatch({ type: "TOGGLE_MESSAGE", payload: msg.id })}
                            onCopy={() => copyToClipboard(JSON.stringify(msg.data), msg.id)}
                            isCopied={copiedId === msg.id}
                        />
                    ))}
                </Content>
            </div>
        </div>
    );
}

type ToolbarProps = {
    statusConfig: { color: string; label: string };
    activeSubscriptions: number;
    strings: string[];
    state: TerminalState;
    lastError: string | null;
    apiDomain: string | null;
    failedCount: number;
    retryFailedSubscriptions: () => void;
    copyAllMessages: () => void;
    copiedId: string | null;
    terminalMessages: TerminalMessage[];
    expandAll: () => void;
    collapseAll: () => void;
    clearAll: () => void;
    closeTerminal: () => void;
};

function Toolbar({
    activeSubscriptions,
    apiDomain,
    clearAll,
    closeTerminal,
    collapseAll,
    copiedId,
    copyAllMessages,
    expandAll,
    failedCount,
    lastError,
    retryFailedSubscriptions,
    state,
    statusConfig,
    strings,
    terminalMessages,
}: ToolbarProps) {
    return (
        <div className="border-emphasis flex h-10 items-center justify-between border-b px-4">
            <div className="flex items-center gap-3 text-xs">
                <span className={cn("h-2 w-2 rounded-full", statusConfig.color)} />
                <span className="text-content-subtle">{statusConfig.label}</span>
                <span className="text-content-placeholder">|</span>
                <span className="text-content-subtle">
                    {activeSubscriptions}/{strings.length} subs
                </span>
                <span className="text-content-placeholder">|</span>
                <span className="text-content-subtle">{state.messages.length} msgs</span>
                {lastError && (
                    <>
                        <span className="text-content-placeholder">|</span>
                        <span className="text-content-error" title={lastError}>
                            ⚠ Error
                        </span>
                    </>
                )}
                {!apiDomain && (
                    <>
                        <span className="text-content-placeholder">|</span>
                        <span className="text-content-warning">No domain</span>
                    </>
                )}
            </div>
            <div className="flex items-center gap-1">
                {failedCount > 0 && (
                    <Button
                        variant="tertiary"
                        size="s"
                        onClick={retryFailedSubscriptions}
                        title={`Retry ${failedCount} failed subscription(s)`}
                        icon="refresh"
                        className="text-content-error"
                    />
                )}
                <Button
                    variant="tertiary"
                    size="s"
                    onClick={copyAllMessages}
                    title={copiedId === "all" ? "Copied!" : `Copy all ${terminalMessages.length} messages`}
                    icon={copiedId === "all" ? "check" : "copy"}
                    disabled={terminalMessages.length === 0}
                />
                <Button variant="tertiary" size="s" onClick={expandAll} title="Expand all" icon="expand" />
                <Button variant="tertiary" size="s" onClick={collapseAll} title="Collapse all" icon="collapse" />
                <Button variant="tertiary" size="s" onClick={clearAll} title="Clear messages" icon="trash" />
                <Button variant="tertiary" size="s" onClick={closeTerminal} title="Close terminal" icon="close" />
            </div>
        </div>
    );
}

type SidebarProps = {
    selectTopic: () => void;
    state: TerminalState;
    messageCountForTopic: number;
    children: React.ReactNode;
};

function Sidebar({ messageCountForTopic, selectTopic, state, children }: SidebarProps) {
    return (
        <div className="border-emphasis w-80 flex-shrink-0 overflow-y-auto border-r">
            <button
                onClick={selectTopic}
                className={cn(
                    "hover:bg-hover hover:text-content-interactive-default flex w-full cursor-pointer items-center justify-between rounded px-2 py-1 text-left text-xs transition-colors",
                    state.selectedTopic === null ? "bg-hover" : "bg-content-default-inverse text-content-default"
                )}
            >
                <span className="font-medium">All</span>
                <Badge variant="gray-subtle" size="s">
                    {messageCountForTopic}
                </Badge>
            </button>
            <div className="border-emphasis border-t" />
            {children}
        </div>
    );
}

type ContentProps = {
    ref: React.RefObject<HTMLDivElement | null>;
    terminalMessages: TerminalMessage[];
    statusConfig: { color: string; label: string };
    connected: boolean;
    children: ReactNode;
};

function Content({ connected, ref, statusConfig, terminalMessages, children }: ContentProps) {
    return (
        <div ref={ref} className="flex-1 overflow-y-auto pt-2 pb-8 font-mono text-xs">
            {terminalMessages.length === 0 ? (
                <div className="text-content-subtle flex h-full flex-col items-center justify-center gap-2">
                    <div className="flex items-center gap-2">
                        <span className={cn("h-2 w-2 rounded-full", statusConfig.color)} />
                        <span>{statusConfig.label}</span>
                    </div>
                    <span className="text-content-placeholder">
                        {connected ? "Waiting for messages..." : "Connect to receive messages"}
                    </span>
                </div>
            ) : (
                <div className="space-y-1">{children}</div>
            )}
        </div>
    );
}

interface MessageRowProps {
    message: TerminalMessage;
    isExpanded: boolean;
    onToggle: () => void;
    onCopy: () => void;
    isCopied: boolean;
}

const REASON_COLORS: Record<string, string> = {
    Nieuw: "text-content-success",
    Wijziging: "text-content-warning",
    Verwijderd: "text-content-error",
};

function MessageRow({ message, isExpanded, onToggle, onCopy, isCopied }: MessageRowProps) {
    const jsonString = JSON.stringify(message.data, null, 2);
    const charCount = jsonString.length;
    const eventData = message.data as { id?: string; reason?: string };
    const eventId = eventData?.id?.slice(0, 8) || message.id.slice(0, 8);
    const reason = eventData?.reason;

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        onCopy();
    };

    return (
        <Collapsible open={isExpanded} onOpenChange={onToggle}>
            <div className="bg-hover/50 hover:bg-hover group rounded px-2 py-1">
                <CollapsibleTrigger className="flex w-full items-center gap-2 text-left">
                    {isExpanded ? (
                        <Icon name="chevron-down" size="xs" className="text-content-subtle flex-shrink-0" />
                    ) : (
                        <Icon name="chevron-right" size="xs" className="text-content-subtle flex-shrink-0" />
                    )}
                    <span className="text-content-subtle flex-shrink-0">{formatTimestamp(message.timestamp)}</span>
                    {reason && (
                        <span className={cn("flex-shrink-0 text-xs", REASON_COLORS[reason] || "text-content-subtle")}>{reason}</span>
                    )}
                    <span className="flex-shrink-0" style={{ color: getTopicColor(message.topic) }}>
                        {`${getTopicRole(message.topic)}.${getTopicChannel(message.topic)}`}
                    </span>
                    <span className="text-content-placeholder flex-shrink-0 font-mono">#{eventId}</span>
                    <span className="flex-1" />
                    <button
                        onClick={handleCopy}
                        className={cn(
                            "flex-shrink-0 rounded p-0.5 transition-opacity",
                            isCopied
                                ? "text-content-success opacity-100"
                                : "text-content-placeholder hover:bg-hover hover:text-content-default opacity-0 group-hover:opacity-100"
                        )}
                        title={isCopied ? "Copied!" : "Copy JSON"}
                    >
                        {isCopied ? <Icon name="check" size="xs" /> : <Icon name="copy" size="xs" />}
                    </button>
                    <span className="text-content-placeholder flex-shrink-0">{charCount}</span>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <pre className="bg-hover text-content-default mt-2 overflow-auto rounded p-2 break-words whitespace-pre-wrap">
                        {jsonString}
                    </pre>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
}
